import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import { useAuth } from '../../../hooks/useAuth';
import { useCart } from '../../../hooks/useCart';
import { useFirebase } from '../../../hooks/useFirebase';

import './order.scss';

import { createOrderRequest } from '../../../api/order/order.action';
import { postOrderFormData, queryGet, queryPost } from '../../../api/tools/tools';
import { getPixelOrderContext, trackEvent } from '../../../utils/analytics/metaPixel';
import { Currency } from '../../../utils/currency/Currency';
import {
  getFromLocalStorage,
  saveToLocalStorage,
  STORAGE_KEYS
} from '../../../utils/storage/storage';
import { Button } from '../../common/button/Button';
import { FrozenDisclaimer } from '../../common/frozen-disclaimer/FrozenDisclaimer';
import { CheckoutAuthGate } from '../auth/CheckoutAuthGate';
import { CompleteProfileModal } from '../complete-profile/CompleteProfileModal';

const calcCouponDiscount = (coupon, subtotal, deliveryFee) => {
  if (!coupon) return 0;
  if (coupon.discountType === 'FREE_DELIVERY') return deliveryFee ?? 0;
  if (coupon.discountType === 'PERCENTAGE') return (subtotal * coupon.discountValue) / 100;
  if (coupon.discountType === 'FIXED') return Math.min(coupon.discountValue, subtotal);
  return 0;
};

const PROCESSING_MESSAGES = [
  'Connecting to payment provider…',
  'Almost there…',
  'Still working on it. This can take a little longer the first time…'
];

const useProcessingMessage = (active) => {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (!active) {
      setIndex(0);
      return;
    }

    const interval = setInterval(() => {
      setIndex((prev) => Math.min(prev + 1, PROCESSING_MESSAGES.length - 1));
    }, 3500);

    return () => clearInterval(interval);
  }, [active]);

  return PROCESSING_MESSAGES[index];
};

export const Order = ({ loading, setLoading }) => {
  const dispatch = useDispatch();
  const { user, authReady, guestEmail, guestPhone, guestName } = useAuth();
  const { remoteConfig } = useFirebase();

  const {
    cart,
    cartTotal,
    setCart,
    setShowOrderModal,
    address,
    serviceFee,
    deliveryFee,
    location,
    subtotal,
    discount
  } = useCart();
  const [cartData, setCartData] = useState({
    cart,
    cartTotal,
    serviceFee
  });

  const [orderError, setOrderError] = useState(null);
  const [unavailableItems, setUnavailableItems] = useState(null);
  const [attemptId, setAttemptId] = useState(null);
  const [profileOverride, setProfileOverride] = useState({});
  const [profileModalDismissed, setProfileModalDismissed] = useState(false);
  const processingMessage = useProcessingMessage(loading);

  const [couponInput, setCouponInput] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponError, setCouponError] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);

  const couponDiscount = calcCouponDiscount(appliedCoupon, subtotal, deliveryFee);
  const finalTotal = Math.max(0, cartTotal - couponDiscount);

  const handleApplyCoupon = async () => {
    const code = couponInput.trim().toUpperCase();
    if (!code) return;
    setCouponError(null);
    setCouponLoading(true);
    try {
      const data = await queryGet(
        remoteConfig,
        `/coupons/validate?code=${encodeURIComponent(code)}`
      );
      setAppliedCoupon(data);
      setCouponInput('');
    } catch {
      setCouponError('Invalid or expired coupon code.');
      setAppliedCoupon(null);
    } finally {
      setCouponLoading(false);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponError(null);
  };

  const handleCancel = () => {
    if (loading) return;
    setCart(cartData.cart);
    setAttemptId(null);
    setOrderError(null);
    setShowOrderModal(false);
  };

  const handlePostFormData = async (order) => {
    await postOrderFormData(order);
  };

  const isGuest = user?.isAnonymous;
  const needsAuthGate = !user || (isGuest && (!guestEmail || !guestPhone || !guestName));

  // `address` (from useCart) is a formatted display string — it never carries contactNumber.
  // The real value lives only in the raw object saved by GoogleAddressSelect.
  const savedAddress = getFromLocalStorage(STORAGE_KEYS.DELIVERY_ADDRESS);
  const name = isGuest
    ? guestName
    : user?.displayName || profileOverride.name || savedAddress?.name || '';
  const contactNumber = isGuest
    ? guestPhone
    : profileOverride.contactNumber || savedAddress?.contactNumber || '';
  const missingName = !needsAuthGate && !name;
  const missingContactNumber = !needsAuthGate && !contactNumber;

  const handleProfileSubmit = (values) => {
    const updates = {};
    if (values.name !== undefined) updates.name = values.name;
    if (values.contactNumber !== undefined) updates.contactNumber = values.contactNumber;

    setProfileOverride((prev) => ({ ...prev, ...updates }));
    saveToLocalStorage(STORAGE_KEYS.DELIVERY_ADDRESS, { ...savedAddress, ...updates });
  };

  const [pendingSubmission, setPendingSubmission] = useState(null);

  const submitOrder = (order, email, restoreCart) => {
    setOrderError(null);
    setUnavailableItems(null);
    setLoading(true);

    dispatch(
      createOrderRequest(order, async (createdOrder, error) => {
        if (createdOrder) {
          if (appliedCoupon) {
            try {
              await queryPost(remoteConfig, '/coupons/redeem', {
                code: appliedCoupon.code,
                orderNumber: createdOrder.orderNumber,
                customerId: user.uid,
                customerEmail: email,
                discountApplied: couponDiscount
              });
            } catch {
              // silent — order already placed
            }
          }
          setPendingSubmission(null);
          saveToLocalStorage(STORAGE_KEYS.PENDING_PURCHASE, {
            orderNumber: createdOrder.orderNumber,
            value: order.amount,
            numItems: restoreCart.items?.length ?? 0
          });
          await handlePostFormData(createdOrder);
          return;
        }

        setLoading(false);
        const unavailable = error?.response?.data?.items;

        if (unavailable?.length > 0) {
          // Restore the cart minus the items that are no longer available, rather than the
          // stale copy as-is — retrying with them still in it would just fail again the same way.
          const unavailableIds = new Set(unavailable.map((item) => item.id));
          setCart({
            ...restoreCart,
            items: restoreCart.items.filter((item) => !unavailableIds.has(item.id))
          });
          setPendingSubmission(null);
          setUnavailableItems(unavailable);
        } else {
          setCart(restoreCart);
          setOrderError(
            'That took longer than expected and didn’t go through. Your details are saved, please try again.'
          );
        }
      })
    );
  };

  const handleOrder = () => {
    if (!cart || Object.keys(cart).length === 0) return;
    if (missingContactNumber || missingName) return;

    const currentAttemptId = attemptId ?? uuidv4();
    if (!attemptId) setAttemptId(currentAttemptId);

    const email = isGuest ? guestEmail : user.email;
    const phone = contactNumber;

    let order = {
      address,
      amount: finalTotal,
      email,
      name,
      clientRequestId: currentAttemptId,
      location: location ? JSON.stringify(location) : null,
      orderData: JSON.stringify(cart),
      userId: user.uid,
      delivery: deliveryFee,
      serviceFees: serviceFee,
      contactNumber: phone,
      subtotal,
      guest: isGuest,
      guestPhone: isGuest ? guestPhone : null,
      ...getPixelOrderContext()
    };

    if (discount) order.discount = discount;
    if (appliedCoupon) order.couponCode = appliedCoupon.code;

    saveToLocalStorage(STORAGE_KEYS.LAST_ORDER_MEAL_PLAN, {
      hadMealPlan: cart.items?.some((item) => item.type === 'meal-plan') ?? false
    });

    const restoreCart = cart;
    trackEvent(
      'InitiateCheckout',
      { value: finalTotal, currency: 'ZAR', num_items: cart.items?.length ?? 0 },
      { email, phone, name, address }
    );
    setCartData({ cart, cartTotal: finalTotal, serviceFee });
    setCart({});
    setPendingSubmission({ order, email, restoreCart });

    submitOrder(order, email, restoreCart);
  };

  const handleRetry = () => {
    if (!pendingSubmission) return;
    submitOrder(pendingSubmission.order, pendingSubmission.email, pendingSubmission.restoreCart);
  };

  if (needsAuthGate) {
    return <CheckoutAuthGate />;
  }

  return (
    <div className="order">
      <h1>Payment</h1>
      <div>
        {address && (
          <div>
            <span>Delivery:</span>
            <Currency value={deliveryFee} />
          </div>
        )}
        {cartData.serviceFee && (
          <div>
            <span>Service Fee:</span>
            <Currency value={cartData.serviceFee} />
          </div>
        )}
        {discount > 0 && (
          <div className="order__discount-row">
            <span>Promotion:</span>
            <span className="order__discount-value">
              -<Currency value={discount} />
            </span>
          </div>
        )}
        {couponDiscount > 0 && (
          <div className="order__discount-row">
            <span>Coupon ({appliedCoupon.code}):</span>
            <span className="order__discount-value">
              -<Currency value={couponDiscount} />
            </span>
          </div>
        )}
        <div>
          <span>Total:</span>
          <Currency value={finalTotal} />
        </div>
        <div>
          <span>Method:</span>
          <span>Payfast</span>
        </div>
      </div>

      {!appliedCoupon ? (
        <div className="order__coupon">
          <div className="order__coupon-row">
            <input
              className="order__coupon-input"
              type="text"
              placeholder="Coupon code"
              value={couponInput}
              onChange={(e) => {
                setCouponInput(e.target.value);
                setCouponError(null);
              }}
              onKeyDown={(e) => e.key === 'Enter' && handleApplyCoupon()}
            />
            <button
              className="order__coupon-btn"
              onClick={handleApplyCoupon}
              disabled={couponLoading || !couponInput.trim()}
            >
              {couponLoading ? '...' : 'Apply'}
            </button>
          </div>
          {couponError && <span className="order__coupon-error">{couponError}</span>}
        </div>
      ) : (
        <div className="order__coupon-applied">
          <span className="order__coupon-tag">{appliedCoupon.code}</span>
          <button className="order__coupon-remove" onClick={handleRemoveCoupon}>
            Remove
          </button>
        </div>
      )}

      {cart.items?.some((item) => item.type === 'meal-plan') && <FrozenDisclaimer />}

      {(missingContactNumber || missingName) && (
        <span className="order__contact-error">
          Please add {missingName && missingContactNumber
            ? 'your name and a contact number'
            : missingName
              ? 'your name'
              : 'a contact number'}{' '}
          before placing your order.{' '}
          {profileModalDismissed && (
            <button
              type="button"
              className="order__contact-error-link"
              onClick={() => setProfileModalDismissed(false)}
            >
              Add now
            </button>
          )}
        </span>
      )}

      {loading && <p className="order__processing-message">{processingMessage}</p>}

      {orderError && <span className="order__contact-error">{orderError}</span>}

      {unavailableItems?.length > 0 && (
        <div className="order__unavailable-items">
          {unavailableItems.map((item) => (
            <span className="order__contact-error" key={item.id ?? item.name}>
              {item.name} is no longer available and was removed from your cart. {item.action}
            </span>
          ))}
        </div>
      )}

      {orderError ? (
        <Button onClick={handleRetry}>TRY AGAIN</Button>
      ) : unavailableItems?.length > 0 ? (
        <Button
          onClick={() => {
            setUnavailableItems(null);
            setShowOrderModal(false);
          }}
        >
          REVIEW CART
        </Button>
      ) : (
        <Button
          disabled={loading || !authReady || missingContactNumber || missingName}
          loading={loading}
          onClick={handleOrder}
        >
          PAY NOW
        </Button>
      )}
      <Button secondary disabled={loading} onClick={handleCancel}>
        CANCEL
      </Button>

      <CompleteProfileModal
        open={(missingName || missingContactNumber) && !profileModalDismissed}
        onOpen={(isOpen) => !isOpen && setProfileModalDismissed(true)}
        needName={missingName}
        needContactNumber={missingContactNumber}
        onSubmit={(values) => {
          handleProfileSubmit(values);
          setProfileModalDismissed(true);
        }}
      />
    </div>
  );
};
